'use client';

import { useTranslations } from 'next-intl';
import { useHomeScanningStatus } from '../../hooks/dashboard/useHomeScanningStatus';
import { toScanningBadgeState } from './home-scanning-status';
import { OperationStatusBadge } from './OperationStatusBadge';
import { useOpenTargetSettings } from './useOpenTargetSettings';

type HomeScanningStatusPillProps = {
  applicationId: string;
};

export function HomeScanningStatusPill({
  applicationId,
}: HomeScanningStatusPillProps) {
  const t = useTranslations('home.scanning');
  const tBlocked = useTranslations(
    'designSystem.signals.lifecycle.status.blockedReason',
  );
  const { status, executionsEnabled, crawlerEnabled } =
    useHomeScanningStatus(applicationId);
  const { openSettings, openSettingsContextMenu } = useOpenTargetSettings();

  if (!status) {
    return null;
  }

  const label = ((): string => {
    if (status.kind === 'paused') {
      return status.reason
        ? t('paused', { reason: tBlocked(status.reason) })
        : t('phase.paused_blocked');
    }

    if (status.kind === 'adaptive') {
      return t('adaptive', { phase: t(`phase.${status.phase}`) });
    }

    return status.enabled ? t('automationOn') : t('automationOff');
  })();
  const onOffLabel = (enabled: boolean) =>
    enabled ? t('tooltip.on') : t('tooltip.off');
  const tooltip = [
    t('tooltip.executions', { state: onOffLabel(executionsEnabled) }),
    t('tooltip.crawler', { state: onOffLabel(crawlerEnabled) }),
    t('tooltip.openAutomationSettings'),
  ].join(' · ');

  return (
    <OperationStatusBadge
      label={label}
      state={toScanningBadgeState(status)}
      tooltip={tooltip}
      onClick={() => openSettings('automation')}
      onContextMenu={(event) => openSettingsContextMenu(event, 'automation')}
    />
  );
}
